/**
 * Reporting what a signed-in buyer viewed and searched for.
 *
 * The server held no record of anybody's browsing, so the notify job could only ever
 * act for people who had explicitly saved a search and asked to be told — measured
 * live, one account in the whole system. Everybody else received nothing, correctly,
 * because there was nothing to act on.
 *
 * Mirrors the iOS ActivityReporter, including the rules it exists to enforce: repeat
 * views of one home collapse, an empty search is not evidence, a failed flush keeps
 * its signals, and an unreachable server cannot grow the buffer without bound.
 */

export interface ActivityFilters {
  cities?: string[];
  price_min?: number;
  price_max?: number;
  beds_min?: number;
  property_types?: string[];
}

export interface ActivitySignal {
  kind: 'view' | 'search';
  listing_id?: string;
  city?: string;
  price?: number;
  beds?: number;
  property_type?: string;
  filters?: ActivityFilters;
  at?: string;
}

/**
 * The most signals held while the server is unreachable.
 *
 * A failed flush keeps its buffer, because these signals are the only record of what
 * the buyer did. But a permanently failing server must not grow it forever, so the
 * OLDEST are dropped: the newest describe what the buyer wants now, which is the
 * whole point of deriving anything.
 */
export const MAX_BUFFERED = 200;

export interface ViewedListing {
  id: string;
  city?: string;
  price?: number;
  beds?: number;
  propertyType?: string;
}

export class ActivityBuffer {
  private buffer: ActivitySignal[] = [];

  constructor(
    private readonly send: (signals: ActivitySignal[]) => Promise<unknown>,
    private readonly batchSize = 12
  ) {}

  get pending(): number {
    return this.buffer.length;
  }

  async recordView(listing: ViewedListing, at: Date = new Date()): Promise<void> {
    // Opening the same home five times is interest, not five homes. Left
    // undeduplicated it would quintuple that listing's city and price in the
    // derivation and drag the derived search toward one address.
    this.buffer = this.buffer.filter(
      (s) => !(s.kind === 'view' && s.listing_id === listing.id)
    );

    this.append({
      kind: 'view',
      listing_id: listing.id,
      city: listing.city || undefined,
      // A price of 0 means "not on file" and says nothing about budget, so it is
      // omitted rather than sent as a number that would drag the floor to zero.
      price: (listing.price ?? 0) > 0 ? listing.price : undefined,
      beds: (listing.beds ?? 0) > 0 ? listing.beds : undefined,
      property_type: listing.propertyType || undefined,
      at: at.toISOString(),
    });

    await this.flushIfFull();
  }

  async recordSearch(filters: ActivityFilters, at: Date = new Date()): Promise<void> {
    const cleaned: ActivityFilters = {};
    if (filters.cities?.length) cleaned.cities = filters.cities;
    if ((filters.price_min ?? 0) > 0) cleaned.price_min = filters.price_min;
    if ((filters.price_max ?? 0) > 0) cleaned.price_max = filters.price_max;
    if ((filters.beds_min ?? 0) > 0) cleaned.beds_min = filters.beds_min;
    if (filters.property_types?.length) cleaned.property_types = filters.property_types;

    // A search with nothing set describes nothing. Reporting it would count toward
    // the derivation's evidence threshold while adding no evidence.
    if (Object.keys(cleaned).length === 0) return;

    this.append({ kind: 'search', filters: cleaned, at: at.toISOString() });
    await this.flushIfFull();
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const batch = this.buffer;
    try {
      await this.send(batch);
      // Only what was actually sent is cleared, so anything recorded while the
      // request was in flight survives rather than being silently lost.
      this.buffer = this.buffer.slice(batch.length);
    } catch {
      // Deliberately kept for the next flush. A browser tab loses connectivity
      // often enough that dropping on the first error would discard most of a
      // buyer's history.
    }
  }

  private append(signal: ActivitySignal): void {
    this.buffer.push(signal);
    if (this.buffer.length > MAX_BUFFERED) {
      this.buffer = this.buffer.slice(this.buffer.length - MAX_BUFFERED);
    }
  }

  private async flushIfFull(): Promise<void> {
    if (this.buffer.length >= this.batchSize) await this.flush();
  }
}
